/**
 * Operator load emails — data layer for the office screens (Phase 17 / Task 17.3, DEC-324).
 *
 * API (task 17.2 / 17.5):
 *   GET  /v1/hauler-operators/:id/site-contacts           contacts + assignments
 *   POST /v1/hauler-operators/:id/site-contacts           add a contact
 *   PATCH /v1/site-contacts/:id                           edit / (de)activate
 *   PUT  /v1/site-contacts/:id/assignments                replace the well / battery set
 *   POST /v1/site-contacts/:id/revoke-ticket-links        kill links already emailed
 *   GET/PUT /v1/hauler-operators/:id/notification-settings  per-customer pair (null = inherit)
 *   GET/PATCH /v1/tenants/notification-settings           hauler-wide defaults + office contact
 *
 * Mutations go through `authedFetch` directly rather than `useApiMutation`, because the
 * screens need the server's per-field reasons (`email: duplicate`,
 * `assignments.<i>.targetId: not_owned`) and `ApiError` carries only the code. The same
 * pattern as stuck-jobs/StuckJobCancelModal.tsx.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { authedFetch, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';

export type SummaryCadence = 'OFF' | 'TWO_HOURS' | 'SIX_HOURS' | 'TWELVE_HOURS' | 'DAILY';
export const SUMMARY_CADENCES: readonly SummaryCadence[] = [
  'OFF',
  'TWO_HOURS',
  'SIX_HOURS',
  'TWELVE_HOURS',
  'DAILY',
];
export const CADENCE_LABEL: Record<SummaryCadence, string> = {
  OFF: 'Off',
  TWO_HOURS: 'Every 2 hours',
  SIX_HOURS: 'Every 6 hours',
  TWELVE_HOURS: 'Every 12 hours',
  DAILY: 'Daily at 6 AM (yesterday)',
};

export type AssignmentType = 'WELL' | 'TANK_BATTERY';

export interface SiteContactAssignment {
  readonly type: AssignmentType;
  readonly targetId: string;
  readonly name: string | null;
  readonly receiveNotifications: boolean;
}

export interface SiteContact {
  readonly id: string;
  readonly operatorId: string;
  readonly name: string;
  readonly email: string;
  readonly phone: string | null;
  readonly role: string | null;
  readonly isActive: boolean;
  readonly portalAccess: boolean;
  readonly unsubscribedAt: string | null;
  /** 17.6 — 'BOUNCING' (hard bounce) or 'COMPLAINED' (marked as spam); senders skip both. */
  readonly emailStatus: 'OK' | 'BOUNCING' | 'COMPLAINED';
  readonly coversAllSites: boolean;
  readonly assignments: readonly SiteContactAssignment[];
  readonly updatedAt: string;
}

export interface OperatorNotificationSettings {
  readonly eachHaulEnabled: boolean | null;
  readonly summaryCadence: SummaryCadence | null;
  readonly tenantDefault: {
    readonly eachHaulEnabled: boolean;
    readonly summaryCadence: SummaryCadence;
  };
  readonly effective: {
    readonly eachHaulEnabled: boolean;
    readonly summaryCadence: SummaryCadence;
  };
}

export interface TenantNotificationSettings {
  readonly defaultEachHaulEnabled: boolean;
  readonly defaultSummaryCadence: SummaryCadence;
  readonly officeEmail: string | null;
  readonly officePhone: string | null;
  readonly requestorLoadEmailsEnabled: boolean;
}

export const contactsKey = (operatorId: string): readonly unknown[] => [
  'site-contacts',
  operatorId,
];
export const settingsKey = (operatorId: string): readonly unknown[] => [
  'operator-notification-settings',
  operatorId,
];
export const TENANT_SETTINGS_KEY = ['tenant-notification-settings'] as const;

/** A refused request, with the server's per-field reasons when it gave any. */
export class LoadEmailRequestError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly fields: ReadonlyArray<{ readonly path: string; readonly reason: string }>,
  ) {
    super(message);
    this.name = 'LoadEmailRequestError';
  }

  /** The reason the server gave for one field, e.g. `reasonFor('email') === 'duplicate'`. */
  reasonFor(path: string): string | null {
    return this.fields.find((f) => f.path === path)?.reason ?? null;
  }
}

interface ErrorEnvelope {
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly fields?: ReadonlyArray<{ readonly path?: unknown; readonly reason?: unknown }>;
  };
}

async function readError(res: Response): Promise<LoadEmailRequestError> {
  try {
    const body = (await res.json()) as ErrorEnvelope;
    const fields = (body.error?.fields ?? []).flatMap((f) =>
      typeof f.path === 'string' && typeof f.reason === 'string'
        ? [{ path: f.path, reason: f.reason }]
        : [],
    );
    const message = typeof body.error?.message === 'string' ? body.error.message : 'Request failed';
    const code = typeof body.error?.code === 'string' ? body.error.code : null;
    return new LoadEmailRequestError(message, code, fields);
  } catch {
    return new LoadEmailRequestError('Request failed', null, []);
  }
}

type Method = 'POST' | 'PUT' | 'PATCH';

function useJsonMutation<TBody, TResponse>(
  method: Method,
  path: (body: TBody) => string,
  invalidate: (body: TBody) => ReadonlyArray<readonly unknown[]>,
): UseMutationResult<TResponse, LoadEmailRequestError, TBody> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<TResponse, LoadEmailRequestError, TBody>({
    mutationFn: async (body: TBody): Promise<TResponse> => {
      // DEC-201: always send a JSON body (an empty object, never undefined).
      const res = await authedFetch(opts, path(body), {
        method,
        body: JSON.stringify((body as { payload?: unknown }).payload ?? {}),
      });
      if (!res.ok) throw await readError(res);
      return (await res.json()) as TResponse;
    },
    onSuccess: (_data, body) => {
      for (const key of invalidate(body)) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useSiteContacts(
  operatorId: string,
): ReturnType<typeof useApiQuery<{ contacts: readonly SiteContact[] }>> {
  return useApiQuery<{ contacts: readonly SiteContact[] }>(
    useApiOpts(),
    `/v1/hauler-operators/${operatorId}/site-contacts`,
    { queryKey: contactsKey(operatorId), staleTime: 15_000 },
  );
}

export function useOperatorNotificationSettings(
  operatorId: string,
): ReturnType<typeof useApiQuery<{ settings: OperatorNotificationSettings }>> {
  return useApiQuery<{ settings: OperatorNotificationSettings }>(
    useApiOpts(),
    `/v1/hauler-operators/${operatorId}/notification-settings`,
    { queryKey: settingsKey(operatorId), staleTime: 15_000 },
  );
}

export function useTenantNotificationSettings(
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ settings: TenantNotificationSettings }>> {
  return useApiQuery<{ settings: TenantNotificationSettings }>(
    useApiOpts(),
    '/v1/tenants/notification-settings',
    { queryKey: TENANT_SETTINGS_KEY, staleTime: 15_000, enabled },
  );
}

export interface ContactFields {
  readonly name: string;
  readonly email: string;
  readonly phone: string | null;
  readonly role: string | null;
}

export function useCreateContact(
  operatorId: string,
): UseMutationResult<{ contact: SiteContact }, LoadEmailRequestError, { payload: ContactFields }> {
  return useJsonMutation(
    'POST',
    () => `/v1/hauler-operators/${operatorId}/site-contacts`,
    () => [contactsKey(operatorId)],
  );
}

export function useUpdateContact(
  operatorId: string,
): UseMutationResult<
  { contact: SiteContact },
  LoadEmailRequestError,
  { contactId: string; payload: Partial<ContactFields> & { isActive?: boolean } }
> {
  return useJsonMutation(
    'PATCH',
    (b) => `/v1/site-contacts/${b.contactId}`,
    () => [contactsKey(operatorId)],
  );
}

export interface AssignmentInput {
  readonly type: AssignmentType;
  readonly targetId: string;
  readonly receiveNotifications: boolean;
}

export function useReplaceAssignments(
  operatorId: string,
): UseMutationResult<
  { contact: SiteContact },
  LoadEmailRequestError,
  { contactId: string; payload: { assignments: readonly AssignmentInput[] } }
> {
  return useJsonMutation(
    'PUT',
    (b) => `/v1/site-contacts/${b.contactId}/assignments`,
    () => [contactsKey(operatorId)],
  );
}

export function useRevokeTicketLinks(): UseMutationResult<
  { revoked: number },
  LoadEmailRequestError,
  { contactId: string }
> {
  return useJsonMutation(
    'POST',
    (b) => `/v1/site-contacts/${b.contactId}/revoke-ticket-links`,
    () => [],
  );
}

export function usePutOperatorSettings(
  operatorId: string,
): UseMutationResult<
  { settings: OperatorNotificationSettings },
  LoadEmailRequestError,
  { payload: { eachHaulEnabled: boolean | null; summaryCadence: SummaryCadence | null } }
> {
  return useJsonMutation(
    'PUT',
    () => `/v1/hauler-operators/${operatorId}/notification-settings`,
    () => [settingsKey(operatorId)],
  );
}

export function usePatchTenantSettings(): UseMutationResult<
  { settings: TenantNotificationSettings },
  LoadEmailRequestError,
  { payload: Partial<TenantNotificationSettings> }
> {
  return useJsonMutation(
    'PATCH',
    () => '/v1/tenants/notification-settings',
    // A tenant default changes every customer's effective settings.
    () => [[...TENANT_SETTINGS_KEY], ['operator-notification-settings']],
  );
}
